"use client";

import { MeshTransmissionMaterial, useGLTF } from "@react-three/drei";
import React, { useMemo } from "react";
import * as THREE from "three";

export type MaterialType = "hybrid";

export interface CrystalProps {
  /** GLTF模型路径 */
  modelPath: string;
  /** 材质类型 */
  materialType?: MaterialType;
  /** 位置 */
  position?: [number, number, number];
  /** 旋转 */
  rotation?: [number, number, number];
  /** 缩放 */
  scale?: number | [number, number, number];
  /** 目标尺寸 */
  targetSize?: number;
  /** 是否投射阴影 */
  castShadow?: boolean;
  /** 是否接收阴影 */
  receiveShadow?: boolean;
  /** 尺寸信息回调 - 当水晶尺寸计算完成时调用 */
  onDimensionsCalculated?: (dimensions: {
    width: number;
    height: number;
    depth: number;
    maxDimension: number;
    scaleRatio: number;
  }) => void;
}

// Transmission材质默认配置 - 模拟自然光下的无色透明水晶
const DEFAULT_TRANSMISSION_CONFIG = {
  /** Base color - 冷白色，模拟无色水晶的自然观感 */
  color: "#f8faff", // 带一点蓝感，让透明材质显得更"冰清"

  /** Transmission - 完全透光 */
  transmission: 1.0,

  /** Thickness - 根据模型尺寸调整的折射深度 */
  thickness: 0.3,
  /** Backside thickness - 背面厚度与正面保持一致 */
  backsideThickness: 0.3,

  /** IOR - 天然水晶/石英的物理折射率 */
  ior: 1.05,

  /** Roughness - 保持高度抛光感 */
  roughness: 0.0,
  /** Metalness - 天然晶体不是金属 */
  metalness: 0.0,

  /** Chromatic aberration - 产生轻微彩虹边缘 */
  chromaticAberration: 0.002,

  /** Anisotropy - 减少模糊获得更清晰效果 */
  anisotropicBlur: 0.01,
  /** Distortion - 无失真 */
  distortion: 0.0,
  /** Distortion scale */
  distortionScale: 0.3,
  /** Temporal distortion - 静态材质 */
  temporalDistortion: 0.0,

  /** Render the backside - 启用背面渲染获得更好效果 */
  backside: true,
  /** Resolution - 高分辨率渲染 */
  resolution: 2048,
  /** Refraction samples - 高质量采样 */
  samples: 16,
};

// Refraction材质默认配置 - 与transmission保持一致的自然水晶观感
const DEFAULT_REFRACTION_CONFIG = {
  /** Base color - 与transmission保持一致的冷白色 */
  color: "#f6f8ff", // 冷白色，模拟无色水晶

  /** Number of ray-cast bounces */
  bounces: 2, // 减少光线弹射次数，降低复杂折射效果
  /** Refraction index - 使用接近天然水晶的物理值 */
  ior: 1.54, // 调整为更接近天然水晶，但保持内部点云清晰
  /** Fresnel (strip light) - 减少边缘效果 */
  fresnel: 0.02, // 大幅降低，减少边缘光带遮挡
  /** RGB shift intensity - 轻微色散效果 */
  aberrationStrength: 0.005, // 适度色散，平衡真实感和清晰度
  /** Fast chroma mode - 启用快速色散模式 */
  fastChroma: true, // 提升性能和清晰度
};

// CubeCamera 配置 - 提高质量以改善refraction效果
const CUBE_CAMERA_CONFIG = {
  resolution: 1024,
  frames: Infinity, // 持续更新环境捕获
};

// HybridTransmission材质默认配置 - 模拟自然光下的无色透明水晶
const DEFAULT_HYBRID_TRANSMISSION_CONFIG = {
  /** Base color - 冷白色，模拟无色水晶的自然观感 */
  color: "#f8faff", // 带一点蓝感，让透明材质显得更"冰清"

  /** Transmission - 完全透光 */
  transmission: 1.0,

  /** Thickness - 根据模型尺寸调整的折射深度 */
  thickness: 0.5,
  /** Backside thickness - 背面厚度与正面保持一致 */
  backsideThickness: 0.5,

  /** IOR - 天然水晶/石英的物理折射率 */
  ior: 1.05,

  /** Roughness - 保持高度抛光感 */
  roughness: 0.0,
  /** Metalness - 天然晶体不是金属 */
  metalness: 0.03,

  /** Chromatic aberration - 产生轻微彩虹边缘 */
  chromaticAberration: 0.003,

  /** Anisotropy - 减少模糊获得更清晰效果 */
  anisotropicBlur: 0.0,
  /** Distortion - 无失真 */
  distortion: 0.01,
  /** Distortion scale */
  distortionScale: 0.3,
  /** Temporal distortion - 静态材质 */
  temporalDistortion: 0.0,

  /** Render the backside - 启用背面渲染获得更好效果 */
  backside: true,
  /** Resolution - 高分辨率渲染 */
  resolution: 2048,
  /** Refraction samples - 高质量采样 */
  samples: 16,
};

export const Crystal = React.memo<CrystalProps>(
  ({
    modelPath,
    materialType = "hybrid",
    position = [0, 0.01, 0],
    rotation = [0, 0, 0],
    scale = 1,
    targetSize = 1.5,
    castShadow = true,
    receiveShadow = true,
    onDimensionsCalculated,
  }) => {
    // GLTF模型加载
    const gltf = useGLTF(modelPath);

    // 处理meshes
    const processedMeshes = useMemo(() => {
      if (!gltf?.scene) return [];

      const clonedScene = gltf.scene.clone();
      const meshArray: THREE.Mesh[] = [];

      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mesh = child as THREE.Mesh;
          mesh.castShadow = castShadow;
          mesh.receiveShadow = receiveShadow;

          // 自动尺寸缩放
          if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
          }
          const bbox = mesh.geometry.boundingBox;
          if (bbox) {
            const width = bbox.max.x - bbox.min.x;
            const height = bbox.max.y - bbox.min.y;
            const depth = bbox.max.z - bbox.min.z;
            const maxDimension = Math.max(width, height, depth);
            const scaleRatio = targetSize / maxDimension;

            // 通过回调传递尺寸信息给外部组件
            if (onDimensionsCalculated) {
              onDimensionsCalculated({
                width: width * scaleRatio,
                height: height * scaleRatio,
                depth: depth * scaleRatio,
                maxDimension: maxDimension * scaleRatio,
                scaleRatio: scaleRatio,
              });
            }

            mesh.scale.setScalar(scaleRatio);
          }

          meshArray.push(mesh);
        }
      });

      return meshArray;
    }, [gltf, castShadow, receiveShadow, targetSize]);

    if (processedMeshes.length === 0) {
      return null;
    }

    // 应用水晶模型标准旋转
    const finalRotation: [number, number, number] = [
      Math.PI / 2 + (Array.isArray(rotation) ? rotation[0] : 0),
      Array.isArray(rotation) ? rotation[1] : 0,
      Array.isArray(rotation) ? rotation[2] : 0,
    ];

    // 渲染基础内容
    const renderMeshes = (materialElement: React.ReactElement) => (
      <group position={position} rotation={finalRotation} scale={scale}>
        {processedMeshes.map((mesh, index) => (
          <mesh
            key={`${modelPath}-mesh-${index}`}
            geometry={mesh.geometry}
            position={mesh.position}
            rotation={mesh.rotation}
            scale={mesh.scale}
            castShadow={castShadow}
            receiveShadow={receiveShadow}
            renderOrder={0} // 水晶渲染优先级低于点云，确保点云在水晶内部可见
          >
            {materialElement}
          </mesh>
        ))}
      </group>
    );

    // hybrid 模式：使用 transmission 渲染，叠加透射和折射效果
    return renderMeshes(
      <MeshTransmissionMaterial {...DEFAULT_HYBRID_TRANSMISSION_CONFIG} />,
    );
  },
);

Crystal.displayName = "Crystal";

export default Crystal;
